// ============================================================
// Platform Database Migrations (shared D1)
// Each migration has up (apply) and down (rollback) SQL.
// Names must be lexicographically ordered (0000_xxx, 0001_xxx, …).
// ============================================================
import { USAGE_REPORTS_DDL } from '../billing/ledger.js';
// --- 0000: Migration tracker bootstrap ---
const m0000_migrations_tracker = {
    name: '0000_migrations_tracker',
    up: [
        `CREATE TABLE IF NOT EXISTS _migrations (
            name TEXT PRIMARY KEY,
            applied_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
    ],
    down: ['DROP TABLE IF EXISTS _migrations'],
};
// --- 0001: Core schema ---
const m0001_init = {
    name: '0001_init',
    up: [
        `CREATE TABLE IF NOT EXISTS tenants (
            id TEXT PRIMARY KEY, name TEXT NOT NULL, slug TEXT NOT NULL UNIQUE,
            plan TEXT NOT NULL DEFAULT 'free', region TEXT NOT NULL DEFAULT 'us',
            ai_addon INTEGER NOT NULL DEFAULT 0, stripe_customer_id TEXT,
            razorpay_customer_id TEXT, monthly_event_count INTEGER NOT NULL DEFAULT 0,
            data_retention_months INTEGER NOT NULL DEFAULT 6,
            created_at TEXT NOT NULL DEFAULT (datetime('now')),
            updated_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_tenants_slug ON tenants(slug)',
        'CREATE INDEX IF NOT EXISTS idx_tenants_stripe ON tenants(stripe_customer_id)',
        `CREATE TABLE IF NOT EXISTS users (
            id TEXT PRIMARY KEY, email TEXT NOT NULL UNIQUE, name TEXT NOT NULL,
            password_hash TEXT NOT NULL, email_verified INTEGER NOT NULL DEFAULT 0,
            mfa_enabled INTEGER NOT NULL DEFAULT 0, mfa_secret TEXT,
            created_at TEXT NOT NULL DEFAULT (datetime('now')),
            updated_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_users_email ON users(email)',
        `CREATE TABLE IF NOT EXISTS tenant_members (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            user_id TEXT NOT NULL REFERENCES users(id) ON DELETE CASCADE,
            role TEXT NOT NULL DEFAULT 'viewer',
            created_at TEXT NOT NULL DEFAULT (datetime('now')),
            UNIQUE(tenant_id, user_id)
        )`,
        'CREATE INDEX IF NOT EXISTS idx_members_tenant ON tenant_members(tenant_id)',
        'CREATE INDEX IF NOT EXISTS idx_members_user ON tenant_members(user_id)',
        `CREATE TABLE IF NOT EXISTS projects (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            name TEXT NOT NULL, domain TEXT NOT NULL,
            timezone TEXT NOT NULL DEFAULT 'UTC',
            public INTEGER NOT NULL DEFAULT 0,
            created_at TEXT NOT NULL DEFAULT (datetime('now')),
            updated_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_projects_tenant ON projects(tenant_id)',
        `CREATE TABLE IF NOT EXISTS api_keys (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            project_id TEXT REFERENCES projects(id) ON DELETE CASCADE,
            name TEXT NOT NULL, key_prefix TEXT NOT NULL, key_hash TEXT NOT NULL,
            scopes TEXT NOT NULL DEFAULT '["ingest"]',
            rate_limit INTEGER NOT NULL DEFAULT 100,
            last_used_at TEXT, expires_at TEXT,
            revoked INTEGER NOT NULL DEFAULT 0,
            created_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_apikeys_tenant ON api_keys(tenant_id)',
        'CREATE INDEX IF NOT EXISTS idx_apikeys_prefix ON api_keys(key_prefix)',
        `CREATE TABLE IF NOT EXISTS subscriptions (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            provider TEXT NOT NULL,
            provider_subscription_id TEXT,
            plan TEXT NOT NULL,
            status TEXT NOT NULL DEFAULT 'active',
            current_period_start TEXT, current_period_end TEXT,
            created_at TEXT NOT NULL DEFAULT (datetime('now')),
            updated_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_subs_tenant ON subscriptions(tenant_id)',
        `CREATE TABLE IF NOT EXISTS audit_logs (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            user_id TEXT REFERENCES users(id),
            action TEXT NOT NULL, resource_type TEXT NOT NULL, resource_id TEXT,
            metadata TEXT, ip_address TEXT,
            created_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_audit_tenant ON audit_logs(tenant_id)',
        'CREATE INDEX IF NOT EXISTS idx_audit_created ON audit_logs(created_at)',
        `CREATE TABLE IF NOT EXISTS agent_configs (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            project_id TEXT REFERENCES projects(id) ON DELETE CASCADE,
            type TEXT NOT NULL, enabled INTEGER NOT NULL DEFAULT 1,
            schedule TEXT, config TEXT NOT NULL DEFAULT '{}',
            created_at TEXT NOT NULL DEFAULT (datetime('now')),
            updated_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_agents_tenant ON agent_configs(tenant_id)',
        `CREATE TABLE IF NOT EXISTS agent_reports (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            agent_config_id TEXT NOT NULL REFERENCES agent_configs(id) ON DELETE CASCADE,
            type TEXT NOT NULL, status TEXT NOT NULL DEFAULT 'pending',
            result TEXT, r2_path TEXT,
            created_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_reports_tenant ON agent_reports(tenant_id)',
        'CREATE INDEX IF NOT EXISTS idx_reports_agent ON agent_reports(agent_config_id)',
        `CREATE TABLE IF NOT EXISTS embed_tokens (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            dashboard_id TEXT NOT NULL, token_hash TEXT NOT NULL,
            allowed_origins TEXT NOT NULL DEFAULT '["*"]',
            expires_at TEXT,
            created_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_embed_tenant ON embed_tokens(tenant_id)',
        `CREATE TABLE IF NOT EXISTS webhook_endpoints (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            url TEXT NOT NULL,
            events TEXT NOT NULL DEFAULT '["*"]',
            secret TEXT NOT NULL,
            active INTEGER NOT NULL DEFAULT 1,
            created_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_webhooks_tenant ON webhook_endpoints(tenant_id)',
    ],
    down: [
        'DROP TABLE IF EXISTS webhook_endpoints',
        'DROP TABLE IF EXISTS embed_tokens',
        'DROP TABLE IF EXISTS agent_reports',
        'DROP TABLE IF EXISTS agent_configs',
        'DROP TABLE IF EXISTS audit_logs',
        'DROP TABLE IF EXISTS subscriptions',
        'DROP TABLE IF EXISTS api_keys',
        'DROP TABLE IF EXISTS projects',
        'DROP TABLE IF EXISTS tenant_members',
        'DROP TABLE IF EXISTS users',
        'DROP TABLE IF EXISTS tenants',
    ],
};
// --- 0002: Billing ---
const m0002_billing = {
    name: '0002_billing',
    up: [
        'ALTER TABLE subscriptions ADD COLUMN events_used INTEGER NOT NULL DEFAULT 0',
        'ALTER TABLE subscriptions ADD COLUMN events_limit INTEGER NOT NULL DEFAULT 10000',
        'ALTER TABLE subscriptions ADD COLUMN ai_credits_used INTEGER NOT NULL DEFAULT 0',
        'ALTER TABLE subscriptions ADD COLUMN ai_credits_limit INTEGER NOT NULL DEFAULT 5',
        'ALTER TABLE subscriptions ADD COLUMN provider_price_id TEXT',
        'ALTER TABLE subscriptions ADD COLUMN cancel_at_period_end INTEGER NOT NULL DEFAULT 0',
        "ALTER TABLE tenants ADD COLUMN country TEXT NOT NULL DEFAULT 'US'",
        `CREATE TABLE IF NOT EXISTS invoices (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            subscription_id TEXT REFERENCES subscriptions(id),
            provider TEXT NOT NULL, provider_invoice_id TEXT,
            amount_cents INTEGER NOT NULL,
            currency TEXT NOT NULL DEFAULT 'usd',
            status TEXT NOT NULL DEFAULT 'draft',
            period_start TEXT, period_end TEXT,
            paid_at TEXT, hosted_url TEXT, pdf_url TEXT,
            created_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_invoices_tenant ON invoices(tenant_id)',
        'CREATE INDEX IF NOT EXISTS idx_invoices_provider ON invoices(provider_invoice_id)',
        `CREATE TABLE IF NOT EXISTS payment_events (
            id TEXT PRIMARY KEY, provider TEXT NOT NULL,
            provider_event_id TEXT NOT NULL UNIQUE,
            event_type TEXT NOT NULL,
            tenant_id TEXT REFERENCES tenants(id),
            payload TEXT NOT NULL,
            processed INTEGER NOT NULL DEFAULT 0,
            created_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_payment_events_provider ON payment_events(provider_event_id)',
        'CREATE INDEX IF NOT EXISTS idx_payment_events_tenant ON payment_events(tenant_id)',
    ],
    down: [
        'DROP INDEX IF EXISTS idx_payment_events_tenant',
        'DROP INDEX IF EXISTS idx_payment_events_provider',
        'DROP TABLE IF EXISTS payment_events',
        'DROP INDEX IF EXISTS idx_invoices_provider',
        'DROP INDEX IF EXISTS idx_invoices_tenant',
        'DROP TABLE IF EXISTS invoices',
        // Note: SQLite doesn't support DROP COLUMN before 3.35.0,
        // but D1 uses a recent enough version. Still, ALTER TABLE DROP COLUMN
        // is the safest approach.
    ],
};
// --- 0003: Agents & Embeds additions ---
const m0003_agents_embeds = {
    name: '0003_agents_embeds',
    up: [
        'ALTER TABLE agent_reports ADD COLUMN tokens_used INTEGER NOT NULL DEFAULT 0',
        'ALTER TABLE agent_reports ADD COLUMN completed_at TEXT',
        'CREATE INDEX IF NOT EXISTS idx_reports_type ON agent_reports(type)',
        'CREATE INDEX IF NOT EXISTS idx_reports_status ON agent_reports(status)',
        'CREATE INDEX IF NOT EXISTS idx_embed_token_hash ON embed_tokens(token_hash)',
    ],
    down: [
        'DROP INDEX IF EXISTS idx_embed_token_hash',
        'DROP INDEX IF EXISTS idx_reports_status',
        'DROP INDEX IF EXISTS idx_reports_type',
    ],
};
// --- 0004: Auth tokens ---
const m0004_auth_tokens = {
    name: '0004_auth_tokens',
    up: [
        `CREATE TABLE IF NOT EXISTS auth_tokens (
            id TEXT PRIMARY KEY,
            user_id TEXT NOT NULL REFERENCES users(id) ON DELETE CASCADE,
            type TEXT NOT NULL,
            token_hash TEXT NOT NULL,
            expires_at TEXT NOT NULL,
            used_at TEXT,
            created_at TEXT NOT NULL DEFAULT (datetime('now'))
        )`,
        'CREATE INDEX IF NOT EXISTS idx_auth_tokens_hash ON auth_tokens(token_hash)',
        'CREATE INDEX IF NOT EXISTS idx_auth_tokens_user ON auth_tokens(user_id, type)',
        'ALTER TABLE users ADD COLUMN last_login_at TEXT',
    ],
    down: [
        'DROP INDEX IF EXISTS idx_auth_tokens_user',
        'DROP INDEX IF EXISTS idx_auth_tokens_hash',
        'DROP TABLE IF EXISTS auth_tokens',
    ],
};
// --- 0005: MFA status ---
const m0005_mfa_status = {
    name: '0005_mfa_status',
    up: [
        'ALTER TABLE users ADD COLUMN recovery_codes_hash TEXT',
        "ALTER TABLE tenants ADD COLUMN status TEXT NOT NULL DEFAULT 'active'",
        'CREATE INDEX IF NOT EXISTS idx_tenants_status ON tenants(status)',
    ],
    down: ['DROP INDEX IF EXISTS idx_tenants_status'],
};
// --- 0006: Superadmin ---
const m0006_superadmin = {
    name: '0006_superadmin',
    up: [
        'ALTER TABLE users ADD COLUMN is_superadmin INTEGER NOT NULL DEFAULT 0',
        'ALTER TABLE users ADD COLUMN force_password_change INTEGER NOT NULL DEFAULT 0',
        'CREATE INDEX IF NOT EXISTS idx_users_superadmin ON users(is_superadmin)',
        // Orphan user fix — create tenant + membership for users without membership
        `INSERT OR IGNORE INTO tenants (id, name, slug, plan, region, created_at, updated_at)
         SELECT 'tn_' || lower(hex(randomblob(8))),
                u.name || '''s Org',
                lower(replace(replace(u.name, ' ', '-'), '''', '')) || '-' || lower(hex(randomblob(3))),
                'free', 'us', datetime('now'), datetime('now')
         FROM users u LEFT JOIN tenant_members tm ON u.id = tm.user_id
         WHERE tm.id IS NULL AND u.is_superadmin = 0`,
        `INSERT OR IGNORE INTO tenant_members (id, tenant_id, user_id, role, created_at)
         SELECT 'tm_' || lower(hex(randomblob(8))), t.id, u.id, 'owner', datetime('now')
         FROM users u LEFT JOIN tenant_members tm ON u.id = tm.user_id
         JOIN tenants t ON t.name = u.name || '''s Org'
              AND t.created_at >= datetime('now', '-1 minute')
         WHERE tm.id IS NULL AND u.is_superadmin = 0`,
    ],
    down: ['DROP INDEX IF EXISTS idx_users_superadmin'],
};
// --- 0007: Google OAuth ---
const m0007_google_oauth = {
    name: '0007_google_oauth',
    up: [
        'ALTER TABLE users ADD COLUMN google_id TEXT DEFAULT NULL',
        'CREATE INDEX IF NOT EXISTS idx_users_google_id ON users(google_id)',
    ],
    down: ['DROP INDEX IF EXISTS idx_users_google_id'],
};
// --- 0008: API keys revoked_at ---
const m0008_api_keys_revoked_at = {
    name: '0008_api_keys_revoked_at',
    up: [
        'ALTER TABLE api_keys ADD COLUMN revoked_at TEXT DEFAULT NULL',
        'UPDATE api_keys SET revoked_at = created_at WHERE revoked = 1',
    ],
    down: [
        // Can't drop column in older SQLite, but D1 supports it.
        // Still, safest to just null out values.
        'UPDATE api_keys SET revoked_at = NULL',
    ],
};
// --- 0009: Subscription notifications ---
const m0009_subscription_notifications = {
    name: '0009_subscription_notifications',
    up: [
        `CREATE TABLE IF NOT EXISTS subscription_notifications (
            id TEXT PRIMARY KEY,
            subscription_id TEXT NOT NULL REFERENCES subscriptions(id) ON DELETE CASCADE,
            tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE,
            notification_type TEXT NOT NULL,
            period_end TEXT NOT NULL,
            sent_at TEXT NOT NULL DEFAULT (datetime('now')),
            UNIQUE(subscription_id, notification_type, period_end)
        )`,
        'CREATE INDEX IF NOT EXISTS idx_sub_notif_tenant ON subscription_notifications(tenant_id)',
        'CREATE INDEX IF NOT EXISTS idx_sub_notif_sub ON subscription_notifications(subscription_id)',
    ],
    down: [
        'DROP INDEX IF EXISTS idx_sub_notif_sub',
        'DROP INDEX IF EXISTS idx_sub_notif_tenant',
        'DROP TABLE IF EXISTS subscription_notifications',
    ],
};
// --- 0010: Tenant settings ---
const m0010_tenant_settings = {
    name: '0010_tenant_settings',
    up: ["ALTER TABLE tenants ADD COLUMN settings TEXT NOT NULL DEFAULT '{}'"],
    down: [
    // SQLite ALTER TABLE DROP COLUMN is supported in D1
    ],
};
// --- 0011: Invoice tax & details ---
const m0011_invoice_tax = {
    name: '0011_invoice_tax',
    up: [
        'ALTER TABLE invoices ADD COLUMN invoice_number TEXT',
        'ALTER TABLE invoices ADD COLUMN subtotal_cents INTEGER',
        'ALTER TABLE invoices ADD COLUMN tax_cents INTEGER DEFAULT 0',
        'ALTER TABLE invoices ADD COLUMN tax_label TEXT',
        'ALTER TABLE invoices ADD COLUMN tax_rate REAL DEFAULT 0',
        'ALTER TABLE invoices ADD COLUMN customer_country TEXT',
        'ALTER TABLE invoices ADD COLUMN invoice_html TEXT',
        'ALTER TABLE invoices ADD COLUMN description TEXT',
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_invoices_number ON invoices(invoice_number)',
        `CREATE TABLE IF NOT EXISTS invoice_counters (
            year INTEGER NOT NULL PRIMARY KEY,
            last_sequence INTEGER NOT NULL DEFAULT 0
        )`,
    ],
    down: [
        'DROP TABLE IF EXISTS invoice_counters',
        'DROP INDEX IF EXISTS idx_invoices_number',
        // SQLite ALTER TABLE DROP COLUMN supported in D1 (SQLite >=3.35)
        'ALTER TABLE invoices DROP COLUMN description',
        'ALTER TABLE invoices DROP COLUMN invoice_html',
        'ALTER TABLE invoices DROP COLUMN customer_country',
        'ALTER TABLE invoices DROP COLUMN tax_rate',
        'ALTER TABLE invoices DROP COLUMN tax_label',
        'ALTER TABLE invoices DROP COLUMN tax_cents',
        'ALTER TABLE invoices DROP COLUMN subtotal_cents',
        'ALTER TABLE invoices DROP COLUMN invoice_number',
    ],
};
// --- 0012: Agent run tracking enhancements ---
const m0012_agent_enhancements = {
    name: '0012_agent_enhancements',
    up: [
        'ALTER TABLE agent_configs ADD COLUMN last_run_at TEXT',
        'ALTER TABLE agent_configs ADD COLUMN last_run_status TEXT',
        "ALTER TABLE agent_reports ADD COLUMN trigger_type TEXT NOT NULL DEFAULT 'manual'",
        'ALTER TABLE agent_reports ADD COLUMN project_id TEXT',
        'CREATE INDEX IF NOT EXISTS idx_reports_created ON agent_reports(created_at)',
    ],
    down: ['DROP INDEX IF EXISTS idx_reports_created'],
};
// --- 0013: Tenant currency ---
const m0013_tenant_currency = {
    name: '0013_tenant_currency',
    up: [
        "ALTER TABLE tenants ADD COLUMN currency TEXT NOT NULL DEFAULT 'USD'",
        "UPDATE tenants SET currency = 'INR' WHERE country = 'IN'",
        "UPDATE tenants SET currency = 'CAD' WHERE country = 'CA'",
        "UPDATE tenants SET currency = 'EUR' WHERE country IN ('DE','FR','AT','NL','BE','ES','IT','PT','IE','FI','GR','LU','MT','CY','SK','SI','EE','LV','LT')",
    ],
    down: [],
};
// --- 0014: Lenses (custom data dimensions per tenant/project) ---
const m0014_lenses = {
    name: '0014_lenses',
    up: [
        `CREATE TABLE IF NOT EXISTS lenses (
            id TEXT PRIMARY KEY,
            tenant_id TEXT NOT NULL,
            project_id TEXT NOT NULL,
            name TEXT NOT NULL,
            template_id TEXT,
            dimensions TEXT NOT NULL DEFAULT '[]',
            metrics TEXT NOT NULL DEFAULT '[]',
            active INTEGER NOT NULL DEFAULT 1,
            created_at TEXT NOT NULL DEFAULT (datetime('now')),
            updated_at TEXT NOT NULL DEFAULT (datetime('now')),
            FOREIGN KEY (tenant_id) REFERENCES tenants(id) ON DELETE CASCADE,
            FOREIGN KEY (project_id) REFERENCES projects(id) ON DELETE CASCADE
        )`,
        'CREATE INDEX IF NOT EXISTS idx_lenses_tenant ON lenses(tenant_id)',
        'CREATE INDEX IF NOT EXISTS idx_lenses_project ON lenses(tenant_id, project_id)',
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_lenses_name ON lenses(tenant_id, project_id, name)',
    ],
    down: ['DROP TABLE IF EXISTS lenses'],
};
// --- 0015: Analytics v2 (sub-tenants, analytics config, definitions, destinations) ---
const m0015_analytics_v2 = {
    name: '0015_analytics_v2',
    up: [
        "CREATE TABLE IF NOT EXISTS sub_tenants ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, external_id TEXT NOT NULL, name TEXT NOT NULL DEFAULT '', status TEXT NOT NULL DEFAULT 'active', metadata TEXT NOT NULL DEFAULT '{}', created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_sub_tenants_external ON sub_tenants(project_id, external_id)',
        'CREATE INDEX IF NOT EXISTS idx_sub_tenants_tenant ON sub_tenants(tenant_id)',
        "CREATE TABLE IF NOT EXISTS project_analytics_config ( project_id TEXT PRIMARY KEY REFERENCES projects(id) ON DELETE CASCADE, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, config TEXT NOT NULL DEFAULT '{}', updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        "CREATE TABLE IF NOT EXISTS analytics_definitions ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, kind TEXT NOT NULL, name TEXT NOT NULL, definition TEXT NOT NULL DEFAULT '{}', created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE INDEX IF NOT EXISTS idx_analytics_definitions_project ON analytics_definitions(project_id, kind)',
        "CREATE TABLE IF NOT EXISTS destinations ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, sub_tenant TEXT, type TEXT NOT NULL DEFAULT 'webhook', name TEXT NOT NULL DEFAULT '', url TEXT NOT NULL, secret_enc TEXT NOT NULL, events TEXT NOT NULL DEFAULT '[\"*\"]', mode TEXT NOT NULL DEFAULT 'batched', active INTEGER NOT NULL DEFAULT 1, disabled_reason TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE INDEX IF NOT EXISTS idx_destinations_project ON destinations(project_id)',
        'ALTER TABLE api_keys ADD COLUMN sub_tenant TEXT',
        'ALTER TABLE api_keys ADD COLUMN key_enc TEXT',
        'CREATE INDEX IF NOT EXISTS idx_apikeys_hash ON api_keys(key_hash)',
    ],
    down: [
        'DROP INDEX IF EXISTS idx_apikeys_hash',
        'ALTER TABLE api_keys DROP COLUMN key_enc',
        'ALTER TABLE api_keys DROP COLUMN sub_tenant',
        'DROP TABLE IF EXISTS destinations',
        'DROP TABLE IF EXISTS analytics_definitions',
        'DROP TABLE IF EXISTS project_analytics_config',
        'DROP TABLE IF EXISTS sub_tenants',
    ],
};
// --- 0016: Partner readiness (per-event realtime override, destination scoping, notifications) ---
const m0016_partner_readiness = {
    name: '0016_partner_readiness',
    up: [
        // Per-event realtime override; existing paid destinations get the default list,
        // free ones (no realtime entitlement) an empty one.
        'ALTER TABLE destinations ADD COLUMN realtime_events TEXT NOT NULL DEFAULT \'["generate_lead","form_submit","purchase"]\'',
        "UPDATE destinations SET realtime_events = '[]' WHERE tenant_id IN (SELECT id FROM tenants WHERE plan = 'free')",
        // GET /destinations?sub_tenant= and sub-tenant-bound credentials.
        'CREATE INDEX IF NOT EXISTS idx_destinations_project_st ON destinations(project_id, sub_tenant)',
        // usage.threshold: once per project per period per threshold.
        'CREATE TABLE IF NOT EXISTS usage_alerts ( project_id TEXT NOT NULL, period TEXT NOT NULL, threshold INTEGER NOT NULL, tenant_id TEXT NOT NULL, used INTEGER NOT NULL DEFAULT 0, cap INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, PRIMARY KEY (project_id, period, threshold) )',
        // Owner emails (usage thresholds, destination auto-disable); id is the dedupe key.
        "CREATE TABLE IF NOT EXISTS notification_outbox ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL, project_id TEXT NOT NULL DEFAULT '', kind TEXT NOT NULL, data TEXT NOT NULL DEFAULT '{}', attempts INTEGER NOT NULL DEFAULT 0, claimed_at INTEGER, last_error TEXT, created_at INTEGER NOT NULL, sent_at INTEGER )",
        'CREATE INDEX IF NOT EXISTS idx_notification_outbox_pending ON notification_outbox(created_at) WHERE sent_at IS NULL',
    ],
    down: [
        'DROP INDEX IF EXISTS idx_notification_outbox_pending',
        'DROP TABLE IF EXISTS notification_outbox',
        'DROP TABLE IF EXISTS usage_alerts',
        'DROP INDEX IF EXISTS idx_destinations_project_st',
        'ALTER TABLE destinations DROP COLUMN realtime_events',
    ],
};
// --- 0017: Warehouse exports (raw data to S3 / GCS / R2 / BigQuery; credentials sealed) ---
const m0017_warehouse_exports = {
    name: '0017_warehouse_exports',
    up: [
        "CREATE TABLE IF NOT EXISTS warehouse_exports ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, name TEXT NOT NULL DEFAULT '', type TEXT NOT NULL, format TEXT NOT NULL DEFAULT 'ndjson.gz', schedule TEXT NOT NULL DEFAULT 'hourly', sub_tenant TEXT, include_internal INTEGER NOT NULL DEFAULT 0, settings TEXT NOT NULL DEFAULT '{}', credentials_enc TEXT NOT NULL, active INTEGER NOT NULL DEFAULT 1, disabled_reason TEXT, start_hour TEXT NOT NULL, cursor_hour TEXT, state TEXT NOT NULL DEFAULT '{}', status TEXT NOT NULL DEFAULT 'new', last_error TEXT, failures INTEGER NOT NULL DEFAULT 0, last_run_at TEXT, last_success_at TEXT, lease_until INTEGER, created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE INDEX IF NOT EXISTS idx_warehouse_exports_project ON warehouse_exports(project_id)',
        'CREATE INDEX IF NOT EXISTS idx_warehouse_exports_active ON warehouse_exports(active, last_run_at)',
    ],
    down: ['DROP TABLE IF EXISTS warehouse_exports'],
};
// --- 0018: Partner API (project provisioning, org management keys, idempotency) ---
const m0018_partner_api = {
    name: '0018_partner_api',
    up: [
        "ALTER TABLE projects ADD COLUMN status TEXT NOT NULL DEFAULT 'active'",
        "ALTER TABLE projects ADD COLUMN currency TEXT NOT NULL DEFAULT 'USD'",
        'ALTER TABLE projects ADD COLUMN external_id TEXT',
        "ALTER TABLE projects ADD COLUMN metadata TEXT NOT NULL DEFAULT '{}'",
        'ALTER TABLE projects ADD COLUMN suspended_at TEXT',
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_projects_external ON projects(tenant_id, external_id) WHERE external_id IS NOT NULL',
        "CREATE TABLE IF NOT EXISTS idempotency_keys ( tenant_id TEXT NOT NULL, principal TEXT NOT NULL, idem_key TEXT NOT NULL, route TEXT NOT NULL, request_hash TEXT NOT NULL, state TEXT NOT NULL DEFAULT 'pending', status_code INTEGER NOT NULL DEFAULT 0, response_enc TEXT, created_at INTEGER NOT NULL, PRIMARY KEY (tenant_id, principal, idem_key) )",
        'CREATE INDEX IF NOT EXISTS idx_idempotency_created ON idempotency_keys(created_at)',
    ],
    down: [
        'DROP TABLE IF EXISTS idempotency_keys',
        'DROP INDEX IF EXISTS idx_projects_external',
        'ALTER TABLE projects DROP COLUMN suspended_at',
        'ALTER TABLE projects DROP COLUMN metadata',
        'ALTER TABLE projects DROP COLUMN external_id',
        'ALTER TABLE projects DROP COLUMN currency',
        'ALTER TABLE projects DROP COLUMN status',
    ],
};
// --- 0019: Ad-platform destinations (Meta CAPI, GA4 MP): non-secret type config ---
const m0019_ad_destinations = {
    name: '0019_ad_destinations',
    up: ["ALTER TABLE destinations ADD COLUMN config TEXT NOT NULL DEFAULT '{}'"],
    down: ['ALTER TABLE destinations DROP COLUMN config'],
};
// --- 0020: Agency / platform wedge (portals, client logins, scheduled reports) ---
const m0020_agency_portal = {
    name: '0020_agency_portal',
    up: [
        "CREATE TABLE IF NOT EXISTS portals ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, slug TEXT NOT NULL, branding TEXT NOT NULL DEFAULT '{}', reports TEXT NOT NULL DEFAULT '[\"overview\",\"timeseries\",\"breakdown\"]', custom_domain TEXT, domain_status TEXT, domain_cf_id TEXT, domain_detail TEXT, status TEXT NOT NULL DEFAULT 'active', created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_portals_slug ON portals(slug)',
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_portals_domain ON portals(custom_domain)',
        'CREATE INDEX IF NOT EXISTS idx_portals_project ON portals(project_id)',
        "CREATE TABLE IF NOT EXISTS portal_users ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, portal_id TEXT NOT NULL REFERENCES portals(id) ON DELETE CASCADE, email TEXT NOT NULL, name TEXT NOT NULL DEFAULT '', password_hash TEXT, role TEXT NOT NULL DEFAULT 'client_viewer', sub_tenants TEXT NOT NULL DEFAULT '[]', status TEXT NOT NULL DEFAULT 'invited', last_login_at TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_portal_users_email ON portal_users(portal_id, email)',
        "CREATE TABLE IF NOT EXISTS portal_tokens ( id TEXT PRIMARY KEY, portal_user_id TEXT NOT NULL REFERENCES portal_users(id) ON DELETE CASCADE, type TEXT NOT NULL, token_hash TEXT NOT NULL, expires_at TEXT NOT NULL, used_at TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_portal_tokens_hash ON portal_tokens(token_hash)',
        "CREATE TABLE IF NOT EXISTS scheduled_reports ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, portal_id TEXT REFERENCES portals(id) ON DELETE CASCADE, name TEXT NOT NULL DEFAULT '', frequency TEXT NOT NULL DEFAULT 'weekly', sub_tenant TEXT, recipients TEXT NOT NULL DEFAULT '[]', include_csv INTEGER NOT NULL DEFAULT 0, unsub_key TEXT NOT NULL, active INTEGER NOT NULL DEFAULT 1, next_run_at TEXT NOT NULL, last_run_at TEXT, last_status TEXT, last_error TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE INDEX IF NOT EXISTS idx_scheduled_reports_due ON scheduled_reports(active, next_run_at)',
        'CREATE INDEX IF NOT EXISTS idx_scheduled_reports_project ON scheduled_reports(project_id)',
        "CREATE TABLE IF NOT EXISTS report_unsubscribes ( schedule_id TEXT NOT NULL REFERENCES scheduled_reports(id) ON DELETE CASCADE, email TEXT NOT NULL, created_at TEXT NOT NULL DEFAULT (datetime('now')), PRIMARY KEY (schedule_id, email) )",
    ],
    down: [
        'DROP TABLE IF EXISTS report_unsubscribes',
        'DROP TABLE IF EXISTS scheduled_reports',
        'DROP TABLE IF EXISTS portal_tokens',
        'DROP TABLE IF EXISTS portal_users',
        'DROP TABLE IF EXISTS portals',
    ],
};
// --- 0021: Metered usage ledger (billing/ledger.ts) ---
// One row per tenant × metric × UTC day: exact count, what Stripe confirmed, the send in flight.
const m0021_usage_reports = {
    name: '0021_usage_reports',
    up: USAGE_REPORTS_DDL,
    down: ['DROP TABLE IF EXISTS usage_reports'],
};
// --- 0022: Team members (invites) + customer audit log index ---
const m0022_team_audit = {
    name: '0022_team_audit',
    up: [
        "CREATE TABLE IF NOT EXISTS org_invites ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, email TEXT NOT NULL, role TEXT NOT NULL DEFAULT 'viewer', token_hash TEXT NOT NULL, invited_by TEXT REFERENCES users(id) ON DELETE SET NULL, expires_at TEXT NOT NULL, accepted_at TEXT, accepted_by TEXT REFERENCES users(id) ON DELETE SET NULL, revoked_at TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_org_invites_token ON org_invites(token_hash)',
        'CREATE INDEX IF NOT EXISTS idx_org_invites_tenant ON org_invites(tenant_id, email)',
        'CREATE INDEX IF NOT EXISTS idx_audit_tenant_created ON audit_logs(tenant_id, created_at, id)',
        // plan.change on every tenants.plan write (billing webhooks, admin, cron, checkout).
        // v1 embed tokens without an expiry get 90 days, with a notice in the org's audit log.
        "INSERT INTO audit_logs (id, tenant_id, user_id, action, resource_type, resource_id, metadata, created_at) SELECT 'aud_' || lower(hex(randomblob(10))), tenant_id, NULL, 'embed_token.expiry_set', 'embed_token', id, json_object('actor', json_object('type', 'system', 'id', 'migration'), 'reason', 'Embed tokens now always expire; this token had no expiry', 'expires_at', strftime('%Y-%m-%dT%H:%M:%fZ', 'now', '+90 days')), strftime('%Y-%m-%dT%H:%M:%fZ', 'now') FROM embed_tokens WHERE expires_at IS NULL",
        "UPDATE embed_tokens SET expires_at = strftime('%Y-%m-%dT%H:%M:%fZ', 'now', '+90 days') WHERE expires_at IS NULL",
        "CREATE TRIGGER IF NOT EXISTS trg_audit_plan_change AFTER UPDATE OF plan ON tenants WHEN OLD.plan IS NOT NEW.plan BEGIN INSERT INTO audit_logs (id, tenant_id, user_id, action, resource_type, resource_id, metadata, ip_address, created_at) VALUES ('aud_' || lower(hex(randomblob(10))), NEW.id, NULL, 'plan.change', 'org', NEW.id, json_object('actor', json_object('type', 'system', 'id', 'billing'), 'from', OLD.plan, 'to', NEW.plan), NULL, strftime('%Y-%m-%dT%H:%M:%fZ', 'now')); END",
    ],
    down: [
        'DROP TRIGGER IF EXISTS trg_audit_plan_change',
        'DROP INDEX IF EXISTS idx_audit_tenant_created',
        'DROP TABLE IF EXISTS org_invites',
    ],
};
// --- 0023: Data lifecycle (lifecycle/deletion.ts, lifecycle/subjects.ts, docs/data-lifecycle.md) ---
// deletion_requests: org closure / project deletion → 30-day grace → purge → certificate.
// No FK to tenants/projects: the record must outlive the rows it deletes.
// privacy_subject_requests: data-subject access / erasure requests, processed by the cron.
const m0023_data_lifecycle = {
    name: '0023_data_lifecycle',
    up: [
        "CREATE TABLE IF NOT EXISTS deletion_requests ( id TEXT PRIMARY KEY, scope TEXT NOT NULL, tenant_id TEXT NOT NULL, project_id TEXT, reason TEXT NOT NULL, requested_by TEXT NOT NULL, requested_at TEXT NOT NULL, purge_after TEXT NOT NULL, deadline TEXT NOT NULL, status TEXT NOT NULL DEFAULT 'scheduled', prior_status TEXT NOT NULL DEFAULT 'active', targets TEXT NOT NULL DEFAULT '[]', cursor TEXT NOT NULL DEFAULT '{}', counts TEXT NOT NULL DEFAULT '{}', started_at TEXT, completed_at TEXT, cancelled_at TEXT, cancelled_by TEXT, certificate TEXT, certificate_sha256 TEXT, account_purge_after TEXT, account_purged_at TEXT, billing_purged_at TEXT )",
        'CREATE INDEX IF NOT EXISTS idx_deletion_due ON deletion_requests(status, purge_after)',
        'CREATE INDEX IF NOT EXISTS idx_deletion_target ON deletion_requests(tenant_id, project_id)',
        'CREATE INDEX IF NOT EXISTS idx_deletion_account ON deletion_requests(account_purged_at, account_purge_after)',
        "CREATE TABLE IF NOT EXISTS privacy_subject_requests ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL, project_id TEXT NOT NULL, kind TEXT NOT NULL, subject_type TEXT NOT NULL, subject TEXT, subject_sha256 TEXT NOT NULL, status TEXT NOT NULL DEFAULT 'queued', cursor TEXT NOT NULL DEFAULT '{}', counts TEXT NOT NULL DEFAULT '{}', linked TEXT NOT NULL DEFAULT '[]', parts INTEGER NOT NULL DEFAULT 0, requested_by TEXT NOT NULL, created_at TEXT NOT NULL, started_at TEXT, completed_at TEXT, expires_at TEXT, error TEXT )",
        'CREATE INDEX IF NOT EXISTS idx_psr_queue ON privacy_subject_requests(status, created_at)',
        'CREATE INDEX IF NOT EXISTS idx_psr_subject ON privacy_subject_requests(project_id, subject_sha256, kind)',
    ],
    down: [
        'DROP TABLE IF EXISTS privacy_subject_requests',
        'DROP TABLE IF EXISTS deletion_requests',
    ],
};
// --- 0024: First-party collection domains (custom ingest hostname per project) ---
const m0024_collection_domains = {
    name: '0024_collection_domains',
    up: [
        "CREATE TABLE IF NOT EXISTS collection_domains ( project_id TEXT PRIMARY KEY REFERENCES projects(id) ON DELETE CASCADE, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, hostname TEXT NOT NULL, status TEXT NOT NULL, ssl_status TEXT, cf_id TEXT, route_id TEXT, detail TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_collection_domains_host ON collection_domains(hostname)',
    ],
    down: ['DROP TABLE IF EXISTS collection_domains'],
};
// --- 0025: Cloudflare cleanup queue (lifecycle/cf-cleanup.ts; drained by yom-api) ---
const m0025_cf_cleanup_queue = {
    name: '0025_cf_cleanup_queue',
    up: [
        "CREATE TABLE IF NOT EXISTS cf_cleanup_queue ( id TEXT PRIMARY KEY, kind TEXT NOT NULL, tenant_id TEXT NOT NULL, project_id TEXT NOT NULL, hostname TEXT NOT NULL, cf_id TEXT, route_id TEXT, status TEXT NOT NULL DEFAULT 'pending', attempts INTEGER NOT NULL DEFAULT 0, next_attempt_at TEXT NOT NULL DEFAULT (datetime('now')), last_error TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')), released_at TEXT )",
        'CREATE INDEX IF NOT EXISTS idx_cf_cleanup_due ON cf_cleanup_queue(status, next_attempt_at)',
    ],
    down: ['DROP TABLE IF EXISTS cf_cleanup_queue'],
};
// --- 0026: YOM Intent segment definitions (docs/intent.md §7) ---
const m0026_intent_segments = {
    name: '0026_intent_segments',
    up: [
        "CREATE TABLE IF NOT EXISTS intent_segments ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, sub_tenant TEXT NOT NULL DEFAULT '', name TEXT NOT NULL, definition TEXT NOT NULL, created_by TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE INDEX IF NOT EXISTS idx_intent_segments_member ON intent_segments(project_id, sub_tenant)',
    ],
    down: ['DROP TABLE IF EXISTS intent_segments'],
};
// --- 0027: YOM Intent audience exports + runs (docs/intent.md §8) ---
const m0027_intent_exports = {
    name: '0027_intent_exports',
    up: [
        "CREATE TABLE IF NOT EXISTS intent_exports ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE, sub_tenant TEXT NOT NULL DEFAULT '', segment_id TEXT NOT NULL, name TEXT NOT NULL, connector TEXT NOT NULL, config TEXT NOT NULL DEFAULT '{}', secret_enc TEXT, explicit_only INTEGER NOT NULL DEFAULT 0, schedule TEXT NOT NULL DEFAULT 'manual', active INTEGER NOT NULL DEFAULT 1, next_run_at TEXT, last_run_id TEXT, created_by TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')), updated_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE INDEX IF NOT EXISTS idx_intent_exports_member ON intent_exports(project_id, sub_tenant)',
        'CREATE INDEX IF NOT EXISTS idx_intent_exports_due ON intent_exports(active, next_run_at)',
        "CREATE TABLE IF NOT EXISTS intent_export_runs ( id TEXT PRIMARY KEY, export_id TEXT NOT NULL, tenant_id TEXT NOT NULL, project_id TEXT NOT NULL, status TEXT NOT NULL, trigger TEXT NOT NULL, cursor TEXT NOT NULL DEFAULT '{}', counts TEXT NOT NULL DEFAULT '{}', report TEXT, error TEXT, parts INTEGER NOT NULL DEFAULT 0, expires_at TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')), started_at TEXT, completed_at TEXT )",
        'CREATE INDEX IF NOT EXISTS idx_intent_export_runs_export ON intent_export_runs(export_id, created_at)',
        'CREATE INDEX IF NOT EXISTS idx_intent_export_runs_open ON intent_export_runs(status, created_at)',
    ],
    down: ['DROP TABLE IF EXISTS intent_export_runs', 'DROP TABLE IF EXISTS intent_exports'],
};
// --- 0028: Partner-managed child orgs, claim links, embed token denylist (docs/partner-api.md) ---
const m0028_partner_child_orgs = {
    name: '0028_partner_child_orgs',
    up: [
        'ALTER TABLE tenants ADD COLUMN parent_tenant_id TEXT',
        'ALTER TABLE tenants ADD COLUMN managed_by_tenant_id TEXT',
        'ALTER TABLE tenants ADD COLUMN external_id TEXT',
        'ALTER TABLE tenants ADD COLUMN claimed_at TEXT',
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_tenants_parent_external ON tenants(parent_tenant_id, external_id) WHERE parent_tenant_id IS NOT NULL',
        'CREATE INDEX IF NOT EXISTS idx_tenants_managed_by ON tenants(managed_by_tenant_id) WHERE managed_by_tenant_id IS NOT NULL',
        "CREATE TABLE IF NOT EXISTS claim_links ( id TEXT PRIMARY KEY, token_hash TEXT NOT NULL, tenant_id TEXT NOT NULL REFERENCES tenants(id) ON DELETE CASCADE, partner_tenant_id TEXT NOT NULL, email TEXT NOT NULL, return_origin TEXT NOT NULL, on_behalf_of TEXT, created_by TEXT NOT NULL, expires_at TEXT NOT NULL, used_at TEXT, used_by TEXT, created_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE UNIQUE INDEX IF NOT EXISTS idx_claim_links_token ON claim_links(token_hash)',
        'CREATE INDEX IF NOT EXISTS idx_claim_links_tenant ON claim_links(tenant_id, created_at)',
        "CREATE TABLE IF NOT EXISTS embed_token_revocations ( jti TEXT PRIMARY KEY, tenant_id TEXT NOT NULL, project_id TEXT NOT NULL, expires_at TEXT NOT NULL, revoked_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE INDEX IF NOT EXISTS idx_embed_revocations_expiry ON embed_token_revocations(expires_at)',
    ],
    down: [
        'DROP TABLE IF EXISTS embed_token_revocations',
        'DROP TABLE IF EXISTS claim_links',
        'DROP INDEX IF EXISTS idx_tenants_managed_by',
        'DROP INDEX IF EXISTS idx_tenants_parent_external',
        'ALTER TABLE tenants DROP COLUMN claimed_at',
        'ALTER TABLE tenants DROP COLUMN external_id',
        'ALTER TABLE tenants DROP COLUMN managed_by_tenant_id',
        'ALTER TABLE tenants DROP COLUMN parent_tenant_id',
    ],
};
// --- 0029: A claimed child org can remove its partner's access (docs/partner-api.md) ---
const m0029_partner_access_revocation = {
    name: '0029_partner_access_revocation',
    up: [
        'ALTER TABLE tenants ADD COLUMN partner_access_revoked_at TEXT',
        'ALTER TABLE tenants ADD COLUMN partner_access_revoked_by TEXT',
    ],
    down: [
        'ALTER TABLE tenants DROP COLUMN partner_access_revoked_by',
        'ALTER TABLE tenants DROP COLUMN partner_access_revoked_at',
    ],
};
// --- 0030: YOM Connect install handshake (docs/integrations/connect.md) ---
const m0030_yom_connect = {
    name: '0030_yom_connect',
    up: [
        "CREATE TABLE IF NOT EXISTS connect_codes ( code_hash TEXT PRIMARY KEY, platform TEXT NOT NULL, store TEXT NOT NULL, return_url TEXT NOT NULL, code_challenge TEXT NOT NULL, tenant_id TEXT NOT NULL, project_id TEXT NOT NULL, sub_tenant TEXT, user_id TEXT NOT NULL, expires_at TEXT NOT NULL, created_at TEXT NOT NULL DEFAULT (datetime('now')) )",
        'CREATE INDEX IF NOT EXISTS idx_connect_codes_expiry ON connect_codes(expires_at)',
        'CREATE TABLE IF NOT EXISTS connect_installs ( id TEXT PRIMARY KEY, tenant_id TEXT NOT NULL, project_id TEXT NOT NULL, sub_tenant TEXT, platform TEXT NOT NULL, store TEXT NOT NULL, pk_key_id TEXT NOT NULL, sk_key_id TEXT NOT NULL, created_by TEXT NOT NULL, created_at TEXT NOT NULL, revoked_at TEXT, revoked_reason TEXT )',
        'CREATE INDEX IF NOT EXISTS idx_connect_installs_project ON connect_installs(project_id, created_at)',
        'CREATE INDEX IF NOT EXISTS idx_connect_installs_store ON connect_installs(project_id, platform, store) WHERE revoked_at IS NULL',
    ],
    down: ['DROP TABLE IF EXISTS connect_installs', 'DROP TABLE IF EXISTS connect_codes'],
};
// --- 0031: Worker route of a portal custom domain (docs/agency-portal.md) ---
const m0031_portal_domain_routes = {
    name: '0031_portal_domain_routes',
    up: ['ALTER TABLE portals ADD COLUMN domain_route_id TEXT'],
    down: ['ALTER TABLE portals DROP COLUMN domain_route_id'],
};
// --- Exported ordered list ---
export const PLATFORM_MIGRATIONS = [
    m0000_migrations_tracker,
    m0001_init,
    m0002_billing,
    m0003_agents_embeds,
    m0004_auth_tokens,
    m0005_mfa_status,
    m0006_superadmin,
    m0007_google_oauth,
    m0008_api_keys_revoked_at,
    m0009_subscription_notifications,
    m0010_tenant_settings,
    m0011_invoice_tax,
    m0012_agent_enhancements,
    m0013_tenant_currency,
    m0014_lenses,
    m0015_analytics_v2,
    m0016_partner_readiness,
    m0017_warehouse_exports,
    m0018_partner_api,
    m0019_ad_destinations,
    m0020_agency_portal,
    m0021_usage_reports,
    m0022_team_audit,
    m0023_data_lifecycle,
    m0024_collection_domains,
    m0025_cf_cleanup_queue,
    m0026_intent_segments,
    m0027_intent_exports,
    m0028_partner_child_orgs,
    m0029_partner_access_revocation,
    m0030_yom_connect,
    m0031_portal_domain_routes,
];
/**
 * Names of migrations that were applied manually (via wrangler d1 migrations apply)
 * before the migration runner existed. Used by seedAppliedMigrations to skip them.
 * Note: The runner normalizes wrangler's `.sql` suffix automatically, so migrations
 * applied by either wrangler or the runner are recognized as applied.
 */
// Frozen at 0014: everything after the baseline must actually run. (Deriving this from
// PLATFORM_MIGRATIONS made every new migration get recorded as applied without executing,
// because the deploy hook seeds the baseline before running pending migrations.)
const BASELINE_LAST = '0014_lenses';
export const PLATFORM_BASELINE_NAMES = PLATFORM_MIGRATIONS.map(m => m.name).filter(name => name <= BASELINE_LAST);
//# sourceMappingURL=platform.js.map